import type { ZodError } from 'zod';
import { type ZodSchema } from 'zod';

import type { Result } from '@/types/api-routes';

export class ZodValidationError extends Error {
  constructor(
    message: string,
    public readonly zodError: ZodError,
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

export function validateApiResponse<T>(data: unknown, schema: ZodSchema<T>): T {
  const result = schema.safeParse(data);

  if (!result.success) {
    const issues = result.error.issues;
    const errorDetails =
      issues.length > 0
        ? issues.map(issue => `${issue.path.join('.')}: ${issue.message}`).join(', ')
        : result.error.message || 'Unknown validation error';

    console.error('API Response Validation Failed:', {
      issues: result.error.issues,
      receivedData: data,
    });

    throw new ZodValidationError(`API response validation failed: ${errorDetails}`, result.error);
  }

  return result.data;
}

export function validateResult<T>(result: Result<unknown>, schema: ZodSchema<T>): Result<T> {
  if (!result.ok) {
    return result;
  }

  const validated = validateApiResponse(result.data, schema);
  return { ok: true, data: validated };
}
